// Form-factor plumbing for the chrome (ui-map-interface.md §12).
//
// Three things live here, and nothing else: the one predicate that says the
// shell is in its sheet form, the two bar measurements the sheet layout
// needs but CSS cannot express, and the geometry of the bottom-left console
// and of the map buttons (R1747). The round unit of the console stands at
// the top left of a desktop (R2879).
//
// **Every figure here is in chrome px** (R1556, §13). `#ui` wears the UI
// scale as CSS `zoom`, so a custom property it publishes is multiplied by
// the scale on the screen. The viewport is therefore read through
// `ui/uiscale.ts`, and the one reader outside the chrome, the canvas
// (`clusterBox`), takes its box back in viewport px.
//
// The console (R218) is the one-owner/two-readers pattern §12.0 already
// uses for `--topbar-h`: the geometry is computed once, here, and published
// twice — as CSS custom properties for the DOM units, and through
// `clusterBox()` for the chart, which is drawn on the canvas and cannot
// read a stylesheet. Before this, four panels each guessed
// another panel's size: the chart carried `CHAT_WIDTH = 360`, the heat
// legend `left: 384px; bottom: 192px`, and the selection panel
// `bottom: 232px`. None of them is a constant any more.

/**
 * The sheet form factor: a phone in portrait, or anything with a finger and
 * not enough height for a right-hand column (a phone on its side).
 *
 * **The same query is written verbatim in ui/styles.css**, above the block
 * labelled "sheet form factor". CSS cannot read a constant and JS cannot
 * read a media block, so the two copies are kept in sync by hand — change
 * one and change the other.
 */
export const SHEET_QUERY = '(max-width: 760px), ((pointer: coarse) and (max-height: 520px))';

// The console's geometry rides presentation.toml [presentation.layout]
// (R312). SHEET_QUERY above stays in code: it is a media query, and
// the stylesheet has to carry the same text verbatim either way.
import {
  LAYOUT_BUTTON_GAP, LAYOUT_CTX_INSET, LAYOUT_DISC_PX, LAYOUT_RAIL_PAD_PX,
  LAYOUT_RAIL_PANEL_PX, LAYOUT_RAIL_PX, LAYOUT_RAIL_WORD_PX, LAYOUT_SCALE_BAR_PX, TARGET_FINGER, WIDTH_CARD, LAYOUT_DISC_PX_SHEET, LAYOUT_GAP, LAYOUT_MAP_ART,
  LAYOUT_MAP_SLOT, LAYOUT_MAP_SLOT_SHEET, LAYOUT_PAD, LAYOUT_ROSE_SLOT, LAYOUT_ROSE_SLOT_SHEET, LAYOUT_VIEW_PX, LAYOUT_VIEW_PX_COARSE,
} from '../gen/presentation';
import { chromeViewport, onUiScale, uiZoom } from './uiscale';
import { COARSE_QUERY } from './pointer';

/** The live match of one media query, or a list that never matches and
 *  never fires outside a browser (unit tests). */
function mediaQuery(query: string): MediaQueryList {
  if (typeof matchMedia === 'function') return matchMedia(query);
  return {
    matches: false,
    media: query,
    onchange: null,
    addEventListener() {},
    removeEventListener() {},
    addListener() {},
    removeListener() {},
    dispatchEvent: () => false,
  } as unknown as MediaQueryList;
}

/** Live match for `SHEET_QUERY`. Off outside a browser (unit tests). */
export const SHEET_MQ: MediaQueryList = mediaQuery(SHEET_QUERY);

/**
 * The landscape rail (R1828, round 083 card 15): a telephone on its side.
 * Every match is a match of `SHEET_QUERY` too (its second clause), so the
 * rail is a form of the sheet. The rail stands only where its cells keep
 * the finger target (`railFits`), so `ui/layout.ts` decides it and writes
 * the class `rail` on `#ui`; the stylesheet reads the class and carries no
 * copy of this query.
 */
export const RAIL_QUERY = '(pointer: coarse) and (max-height: 520px) and (orientation: landscape)';

/** Live match for `RAIL_QUERY`. Off outside a browser (unit tests). */
export const RAIL_MQ: MediaQueryList = mediaQuery(RAIL_QUERY);

/**
 * Is the shell in its sheet form factor now?
 *
 * `SHEET_MQ` is a module constant evaluated at import, so a caller that
 * reads it directly cannot be steered by a test and cannot be read twice
 * with two answers. Behaviour therefore asks this function, and CSS asks
 * the same query: the two lanes switch on one predicate.
 */
export function isSheet(): boolean {
  return SHEET_MQ.matches;
}

/**
 * Publishes the top bar's, the dock's and the tab bar's measured heights
 * on #ui as `--topbar-h`, `--dock-h` and `--tabs-h`, and the dock's width
 * as `--dock-w`.
 *
 * The sheet layout has to stack: the selection panel sits above the dock,
 * the banners below the top bar, the telephone OPTIONS menu stops at the
 * tab bar (R1754), and the bars change height when the bar wraps, the
 * sheet expands or the safe area moves. CSS has no way to ask, so a
 * ResizeObserver answers — event-driven, never a timer (§10.3).
 */
export function installLayoutVars() {
  if (typeof ResizeObserver !== 'function') return;
  const ui = document.getElementById('ui');
  const topbar = document.getElementById('topbar');
  const dock = document.getElementById('dock');
  const tabs = document.getElementById('tabs');
  const selection = document.getElementById('selection');
  const top = TOP_CHROME.slice(1).map((id) => document.getElementById(id));
  if (!ui) return;
  const probe = safeProbe(ui);
  const obs = new ResizeObserver((entries) => {
    let recluster = false;
    for (const e of entries) {
      const el = e.target as HTMLElement;
      // #ui is the viewport: it is `inset: 0`, so it changes size when the
      // window does. Watching it here rather than putting a `resize`
      // listener on the window keeps every measurement on one observer, and
      // leaves the overlay with no window subscription to take off again.
      if (el === ui) { recluster = true; continue; }
      // The safe area moved: a rotation, or a new UI scale (R1828).
      if (el === probe) { insets = readInsets(probe); recluster = true; continue; }
      // The context sheet (§12.0): its closed height lifts the console on
      // a telephone, and a raised sheet keeps the closed height, because a
      // raised sheet stands over the console and must not move it (R237).
      if (el === selection) { if (noteContextSheet(el)) recluster = true; continue; }
      // The desktop round unit stands under the group bar and the DEV
      // STATS line (R2879).
      if (top.includes(el)) { if (!SHEET_MQ.matches) recluster = true; continue; }
      // offsetHeight, not contentRect: the border box is what another panel
      // has to clear, and both bars carry padding and a border.
      const name = el === topbar ? '--topbar-h' : el === tabs ? '--tabs-h' : '--dock-h';
      ui.style.setProperty(name, `${el.offsetHeight}px`);
      // Beside the rail the round unit stands under the readout strip
      // where it does not fit over the bar (round 090 card 09 C), and on a
      // desktop it always stands under the line (R2879).
      if (el === topbar && (railNow || !SHEET_MQ.matches)) recluster = true;
      // The dock's width, measured from the right edge of the viewport so
      // that its own margin is inside the figure (R556). A card in the
      // bottom-right corner of the map stands left of the dock on a
      // desktop, and the sheet form factor reads `--dock-h` instead. The
      // layout offset and not the client rectangle: the dock-in slides the
      // dock in from off screen with a transform, and the observer's first
      // call lands while it is still out there.
      if (el === dock) {
        dockW = Math.max(0, Math.round(chromeViewport().w - el.offsetLeft));
        ui.style.setProperty('--dock-w', `${dockW}px`);
        // The mirrored desktop measures its right side from the dock's
        // left edge (R2883), and the desktop VIEW and the chart stack stand
        // one pad left of it (R2904).
        if (!SHEET_MQ.matches) recluster = true;
      }
      // The dock's height no longer moves the console (R237): the sheet is
      // drawn over it and the map buttons hold their place. `--dock-h` is
      // still published, because it is the measurement a panel needs to
      // clear the sheet, and nothing else here reads it.
    }
    // The console stands on the sheet's bar in the sheet form factor
    // (R318), and the bar's first real measurement arrives here. A drag of
    // the sheet fires this observer too, but the grip and the tab bar do
    // not change height during one, so the guard keeps the console still
    // through a drag (R237).
    if (recluster || consoleFloor() !== floorB) relayoutCluster();
  });
  obs.observe(ui);
  // The border box (R1772): the figures are offsetHeight, and a change of
  // padding alone (the tab bar under the telephone menu, the safe area of
  // the readout line) leaves the content box as it was, so the default
  // box never told the observer.
  const border = { box: 'border-box' } as const;
  if (topbar) obs.observe(topbar, border);
  if (dock) obs.observe(dock, border);
  if (tabs) obs.observe(tabs, border);
  if (selection) obs.observe(selection, border);
  for (const el of top) if (el) obs.observe(el, border);
  obs.observe(probe, border);
  insets = readInsets(probe);
}

// --- the safe area (R1828) ---------------------------------------------------

/** The safe insets of the screen in chrome px: `env(safe-area-inset-*)`
 *  divided by the UI scale. */
export interface Insets { t: number; r: number; b: number; l: number }
let insets: Insets = { t: 0, r: 0, b: 0, l: 0 };

/** The safe insets at the last measure. */
export function safeInsets(): Insets { return { ...insets }; }

/**
 * The probe of the safe area: a hidden box inside `#ui` whose width is the
 * left inset, whose height is the bottom inset, and whose right and top
 * borders are the right and the top inset (`#safeprobe` in the stylesheet,
 * from the tokens `--safe-*`). A script cannot read `env()`, and a box
 * inside the zoomed root measures in chrome px in every browser (R1556).
 */
function safeProbe(ui: HTMLElement): HTMLElement {
  let probe = document.getElementById('safeprobe');
  if (!probe) {
    probe = document.createElement('div');
    probe.id = 'safeprobe';
    probe.setAttribute('aria-hidden', 'true');
    ui.appendChild(probe);
  }
  return probe;
}

/** The box a probe measures. */
export interface ProbeBox { clientWidth: number; clientHeight: number; offsetWidth: number; offsetHeight: number }

/** The four insets from the probe: the client box is the left and the
 *  bottom inset, and the border box less it the right and the top one. */
export function readInsets(probe: ProbeBox): Insets {
  const l = probe.clientWidth;
  const b = probe.clientHeight;
  return { l, b, r: Math.max(0, probe.offsetWidth - l), t: Math.max(0, probe.offsetHeight - b) };
}

// --- the bottom-left console (ui-map-interface.md §12.0, R218) -------------
//
// Every distance rides presentation.toml [presentation.layout] (R312).

/** Inset from the screen edge. On a telephone it is also the lift of the
 *  console and the map buttons over the dock's bar (R1747). */
export const PAD = LAYOUT_PAD;
/** Gutter between two panels of the console. */
export const GAP = LAYOUT_GAP;
/** The VIEW button on a fine pointer (R1747, card 08 of mockup round 082). */
export const VIEW_PX = LAYOUT_VIEW_PX;
/**
 * The VIEW button on a coarse pointer.
 *
 * The VIEW button is the one way to the chat, the chart, the heatmap
 * legend and the faction key (R1747), so it is a primary control and takes
 * more than the shell's general 40 px touch floor (§12.0, R135).
 */
export const VIEW_PX_COARSE = LAYOUT_VIEW_PX_COARSE;
/** The gutter under the VIEW button where a unit lifts it, and
 *  between the button and the VIEW panel. The hold plate that stood over
 *  VIEW is gone: a press on the compass rose is the way home (R1758). */
export const BUTTON_GAP = LAYOUT_BUTTON_GAP;

/** Live match for a finger. Sizing is orthogonal to width (§12.0): a tablet
 *  keeps the desktop layout and still gets the bigger controls. */
const COARSE_MQ: MediaQueryList = mediaQuery(COARSE_QUERY);

/** The VIEW button's size for the pointer this session is driven with. */
export function viewPx(): number {
  return COARSE_MQ.matches ? VIEW_PX_COARSE : VIEW_PX;
}

/** The units of the console, in the order the packer places their
 *  panels. The order is fixed: a panel that moves is a panel the eye has
 *  to look for.
 *
 *  `chat` is the chat box of a desktop whose chat setting is SMALL or
 *  LARGE (R1838): it takes the lower-left corner. VIEW stands on the
 *  chart strip at the other corner (R2904). DOCKED and a telephone keep the chat in the dock
 *  (R1799), and the unit then measures nothing.
 *
 *  `rose` is the compass rose of the chart furniture (R555). It has no
 *  toggle: the canvas draws it, and the console only holds its slot, so
 *  that nothing packs over it. The heat legend (R1806) left the console.
 *  Its one DOM is the rose press, a transparent button the stylesheet
 *  stands on the published slot (R1758). On a desktop the packer leaves
 *  it out: it stands at the top left, under the readout line and the
 *  group bar (`deskUnit`, R2879).
 *
 *  `map` is the chart, and it is no unit of its own any more (R1801): the
 *  round minimap is a disc in the frame of the rose, so the `rose` unit
 *  holds both, and the `map` slot is the disc's square in the centre of
 *  the rose's slot. The unit takes the larger `map_slot` while the chart
 *  is open and the rose's own `rose_slot` while it is shut. */
export type ConsoleUnit = 'chat' | 'map' | 'rose';
export const CONSOLE_UNITS: readonly ConsoleUnit[] = ['chat', 'map', 'rose'];
/** The units the packer places (R1801, R1838). The chart stands in the
 *  rose's unit, so the packer places the chat box and the rose. */
const PACKED_UNITS: readonly ConsoleUnit[] = CONSOLE_UNITS.filter((u) => u !== 'map');

/** The chart's own drawing size, in chart pixels. Its slot may be smaller
 *  (the sheet form factor), and the chart scales to fit. */
export const MAP_ART = LAYOUT_MAP_ART;
/** The square of the round unit while the chart is open (R1801): the
 *  disc and the rim of the rose round it. A telephone takes the smaller
 *  pair. */
export const MAP_SLOT = LAYOUT_MAP_SLOT;
export const MAP_SLOT_SHEET = LAYOUT_MAP_SLOT_SHEET;
/** The disc of the round minimap in the centre of that square (R1801). */
export const DISC_PX = LAYOUT_DISC_PX;
export const DISC_PX_SHEET = LAYOUT_DISC_PX_SHEET;
/** The compass rose's own square (R1745), which the unit takes while the
 *  chart is shut and the rose stands alone (R1801). */
export const ROSE_SLOT = LAYOUT_ROSE_SLOT;
export const ROSE_SLOT_SHEET = LAYOUT_ROSE_SLOT_SHEET;

/** A panel's place in the console: `x` from the left edge of the viewport,
 *  `y` from the bottom edge, because every panel of the console stands on
 *  one floor. */
export interface Slot { x: number; y: number; w: number; h: number }

/** One unit's size as the packer is handed it. */
export interface UnitSize { unit: ConsoleUnit; w: number; h: number }

/**
 * Where the packer may put the units (R1747). `floor` is the line the
 * first row stands on, in px from the bottom of the viewport. `trail` is a
 * width at the right of every row that the packer keeps free: the VIEW
 * button at the right edge of a telephone. The desktop VIEW stands on the
 * chart strip (R2904), so the packer keeps no box free for it.
 */
export interface PackRoom {
  vw: number;
  floor: number;
  /** The left edge of every row: 0, or the rail of a telephone on its
   *  side (R1828). */
  left?: number;
  trail?: number;
  /** The mirrored desktop (R2883): the rows pack from the right edge `vw`
   *  (the dock's left edge) toward the left, so each slot is the slot of
   *  the plain pack flipped inside `0..vw`. */
  mirror?: boolean;
}

/**
 * Packs the open units into rows over the floor: the whole geometry of the
 * console.
 *
 * Panels pack from `PAD` with `GAP` between them, in the fixed unit order,
 * and **wrap to a new row above when the next one would not fit the
 * viewport**. Rows stack upward.
 *
 * It is pure, and it is the whole geometry — a test pins it without a
 * browser, the way `pick()` in render/minimap.ts is split out.
 *
 * @returns the placed slots, and the height of the whole console measured
 *   from the bottom of the viewport (what `#selection` has to clear).
 */
export function packCluster(
  open: readonly UnitSize[],
  room: PackRoom,
): { slots: Partial<Record<ConsoleUnit, Slot>>; height: number } {
  const slots: Partial<Record<ConsoleUnit, Slot>> = {};
  const right = room.vw - PAD - (room.trail ?? 0);
  const x0 = (room.left ?? 0) + PAD;
  let x = x0;
  let y = room.floor;
  let rowH = 0;
  for (const u of open) {
    // A row that is still empty takes the panel whatever its width: a panel
    // wider than the screen must overflow once, not wrap for ever.
    if (rowH > 0 && x + u.w > right) {
      y += rowH + GAP;
      x = x0;
      rowH = 0;
    }
    slots[u.unit] = { x, y, w: u.w, h: u.h };
    x += u.w + GAP;
    rowH = Math.max(rowH, u.h);
  }
  // The mirrored desktop flips every slot inside the room (R2883).
  if (room.mirror) for (const s of Object.values(slots)) if (s) s.x = flipX(s.x, s.w, room.vw);
  // Rows stack upward, so the last row placed is the top one.
  return { slots, height: rowH > 0 ? y + rowH : room.floor };
}

/**
 * The left edge of a box `w` wide whose place is `x` in the plain layout,
 * mirrored inside `0..edge` (R2883): the gap at the left becomes the gap at
 * the right. `edge` is the dock's left edge on a mirrored desktop. Pure.
 */
export function flipX(x: number, w: number, edge: number): number {
  return edge - x - w;
}

/**
 * The box of the map buttons (R1747): `x` from the left and `b` from the
 * bottom of the viewport, `w` and `h` its size. It holds the VIEW button
 * alone: at the right edge of a telephone, on the chart strip of a
 * desktop (R2904), and in the lower-left corner of an arcade desktop. The
 * hold plate that stood over VIEW on a telephone is gone (R1758).
 */
export interface MapButtons { x: number; b: number; w: number; h: number }

/**
 * Where the map buttons stand (R1747, card 08 of mockup round 082).
 *
 * A telephone stands the VIEW button at the right edge, on the floor. **A
 * unit that reaches under the button lifts it**, one `BUTTON_GAP` over its
 * top. The packer keeps every unit of a telephone left of the button
 * (`trail`), so only a unit wider than the room beside it can lift it.
 *
 * The arcade match (R1935) stands its desktop VIEW in the lower-left
 * corner with no console unit, and in the lower-right corner of a mirrored
 * desktop (R2883; `vw` is then the dock's left edge). The desktop VIEW of
 * the game stands on the chart strip instead (`stripButtons`, R2904).
 *
 * It is pure, like `packCluster`.
 */
export function placeMapButtons(
  slots: Partial<Record<ConsoleUnit, Slot>>, vw: number, floor: number, sheet: boolean,
  view: number = viewPx(), mirror = false,
): MapButtons {
  const x = sheet || mirror ? vw - PAD - view : PAD;
  return { x, b: floorOver(x, x + view, boxesOf(slots), floor, BUTTON_GAP), w: view, h: view };
}

/** The slots that stand, as a list. */
function boxesOf(slots: Partial<Record<ConsoleUnit, Slot>>): Slot[] {
  return Object.values(slots).filter((s) => s !== undefined);
}

/**
 * Where the desktop VIEW button of the game stands (R2904): on top of the
 * chart strip of the chart stack (R2181), at the strip's outer edge one
 * pad inside the dock (`dockL` is the dock's left edge), one `BUTTON_GAP`
 * over the strip's top `stripTop`. It moves with the strip when the legend
 * folds or grows. A mirrored desktop (R2883) stands it on the strip at the
 * left edge. While no card of the strip stands (`stripTop` null: the
 * LEGEND tile is off, or no card rendered yet), VIEW stands in that corner
 * on the floor, lifted over an open unit under it as the column is
 * (R2057). It is pure, like `packCluster`.
 */
export function stripButtons(
  slots: Partial<Record<ConsoleUnit, Slot>>, dockL: number, floor: number, stripTop: number | null,
  view: number = viewPx(), mirror = false,
): MapButtons {
  const x = mirror ? PAD : dockL - PAD - view;
  const b = stripTop !== null ? stripTop + BUTTON_GAP
    : floorOver(x, x + view, boxesOf(slots), floor, BUTTON_GAP);
  return { x, b, w: view, h: view };
}

/** Which units are open. The overlay answers; the console never decides.
 *  A page with no chat box leaves `chat` out. */
export type ConsoleState = Record<Exclude<ConsoleUnit, 'chat'>, boolean> & { chat?: boolean };

let readConsole: () => ConsoleState = () => ({ map: true, rose: true });
/** The last computed slot of every unit, open or not. A minimized unit
 *  keeps its slot: that is the box animation A collapses out of. */
let slots: Partial<Record<ConsoleUnit, Slot>> = {};
let openNow: ConsoleState = { map: true, rose: true };
let buttons: MapButtons = { x: PAD, b: PAD, w: VIEW_PX, h: VIEW_PX };
let clusterH = PAD;
/** The top of the chart strip in chrome px from the bottom of the
 *  viewport, or null while no card of it stands (R2904). The chart stack
 *  hands it in through `setStripTop`. */
let stripTop: number | null = null;
/** The foot of the chrome on the top edge of a desktop (`topFoot`), which
 *  the context card stops under (R2904). */
let topF = 0;
/** The top that a desktop panel at the left keeps free, from the top of
 *  the viewport (R2879): one gap under the open round unit at the top
 *  left, else 0. The assign toast, the marks key and the ledger notices
 *  stand under it. The context card (R2904) and the casino pane (R2971)
 *  may stand over the unit. */
let unitT = 0;
let floorB = PAD;
/** The MIRRORED LAYOUT setting (R2883), as the overlay hands it in. */
let mirrorOn = false;
/** The desktop stands mirrored now: the setting, and no sheet form. */
let mirrorNow = false;
/** The dock's measured width from the right edge of the viewport, in
 *  chrome px (`--dock-w`). */
let dockW = 0;
/** The right edge of the map a mirrored desktop measures from: the dock's
 *  left edge, else the viewport's right edge. */
let edgeR = 0;
const clusterListeners: (() => void)[] = [];
/** The one-time subscription of `installCluster`, so a second overlay does
 *  not stack a second copy of it. */
let clusterWired = false;

/** The viewport in chrome px, with an answer for a test that has no
 *  window. */
function vw(): number { return chromeViewport().w; }
function vh(): number { return chromeViewport().h; }

/**
 * The height of the sheet's own bar on the bottom edge: the grip under
 * the setting of three heights (R1748), the siege row or the peek line
 * while one stands, the queue line, the group row while a slot holds a
 * fleet (R1817), and the tab bar, which stay on the
 * glass at every stop of the sheet (R233, R1555, R1749, R1753, R1763). The
 * overlay's collapsed stop is this height too. A grip that the setting
 * hides measures 0.
 *
 * The console and the map buttons stand one pad over it in the sheet form
 * factor (R318, R1747): the tab bar is full-width, `bottom: 0` and a
 * z-index over the console (R237), so a floor on the screen edge would put
 * them under the closed sheet. The elements are measured rather than
 * restated: the tab bar's height is its 44 px row plus the home
 * indicator's inset, and neither figure belongs here.
 */
export function sheetBarH(): number {
  if (typeof document === 'undefined') return 0;
  const grip = document.getElementById('dockgrip');
  const tabs = document.getElementById('tabs');
  // The rail of a telephone on its side is no part of the bar (R1828).
  // The group row over the tab bar (R1817, round 085 card 12 A) stands
  // only while a slot holds a fleet, so the console, VIEW and the context
  // sheet rise with it through this one figure.
  return (grip?.offsetHeight ?? 0) + slotBarH() + chatBarH() + queueLineH() + outerH('grouprow')
    + (railNow ? 0 : tabs?.offsetHeight ?? 0);
}

/** The height of one element of the bar with its margins while it stands,
 *  else 0. */
function outerH(id: string): number {
  const el = document.getElementById(id);
  if (!el || el.offsetHeight <= 0) return 0;
  const cs = getComputedStyle(el);
  return el.offsetHeight + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
}

/**
 * The chat in the sheet's bar (R1799): the chat band while it stands, else
 * the chat line while it takes the slot of the peek line. Both stay on the
 * closed sheet, so the console and the VIEW button stand over them.
 */
function chatBarH(): number {
  return outerH('chat') + outerH('chatline');
}

/**
 * The slot over the queue line (R1753): the siege row while a warning
 * holds, with its margins, else the peek line. The row stays on the closed
 * sheet, so the console stands over it as it stands over the peek line.
 */
function slotBarH(): number {
  const siege = document.getElementById('siegerow');
  return siege && siege.offsetHeight > 0 ? outerH('siegerow') : peekBarH();
}

/** One line of a host on the closed sheet: the line's height with the
 *  host's own rules (the border box less the client box), or 0 while the
 *  host or the line does not stand. */
function lineH(hostId: string, line: string): number {
  const host = document.getElementById(hostId);
  const row = host?.querySelector<HTMLElement>(line);
  if (!host || !row) return 0;
  return row.offsetHeight + host.offsetHeight - host.clientHeight;
}

/**
 * The queue line on the closed sheet (R1763): the head line of the queue
 * strip with the strip's own rules, or 0 before the first Empire frame
 * writes it. The line has one height at every stop, and the jobs under it
 * never count, so an open queue does not move the console (R237).
 */
function queueLineH(): number {
  return lineH('queuestrip', '.qline');
}

/**
 * The peek line on the closed sheet (R1555, R1749): the one row of the
 * advisor line, which carries the sea trials (R2131), or 0 while it does
 * not stand. The row has one height at every stop, because its width does
 * not change, so the console and the map buttons move only when the line
 * comes, goes, wraps to a second row or folds open.
 */
function peekBarH(): number {
  return lineH('peekline', '.advrow');
}

/** The floor of the console and of the map buttons now: one pad over the
 *  screen edge, or one pad over the sheet's bar (R1747): the tab bar, the
 *  queue line and the peek line or the siege row (R1749, R1763). It
 *  changes only with the form factor and the bar's own height, never with
 *  the sheet's stop — the console keeps one place (R237). */
function consoleFloor(): number {
  if (!SHEET_MQ.matches) return PAD;
  // The rail (R1828): the bar stands at the foot of the sheet column beside
  // the rail, over the home indicator. A context card at the right edge
  // lifts no console; a card over the column's bar lifts it as the closed
  // sheet of a portrait telephone does (round 090 card 09 C).
  if (railNow) return insets.b + (ctxBeside ? sheetBarH() + PAD : sheetConsoleFloor(sheetBarH(), ctxClosedH));
  return sheetConsoleFloor(sheetBarH(), ctxClosedH);
}

/**
 * How big a unit is.
 *
 * The chart and the rose are not DOM, so their slot is stated above. The
 * chat box is measured rather than guessed (R1838): CSS owns its two
 * sizes, and a narrow desktop takes a smaller pair.
 */
function unitSize(u: ConsoleUnit, sheet: boolean, round: boolean): { w: number; h: number } | null {
  // The rose is a square of its own (R1745), and the larger square of the
  // round minimap while the chart is open (R1801).
  if (u === 'rose') {
    const s = round ? (sheet ? MAP_SLOT_SHEET : MAP_SLOT) : (sheet ? ROSE_SLOT_SHEET : ROSE_SLOT);
    return { w: s, h: s };
  }
  // The chat box of SMALL and LARGE (R1838). It wears the class `box`
  // only on a desktop; the band and the pane stand in the dock. A box
  // that animation A minimized keeps its size, because it shrinks by
  // `scale`, so a closed box keeps the slot it closes out of.
  if (u === 'chat' && !sheet && typeof document !== 'undefined') {
    const el = document.getElementById('chat');
    if (!el || !el.classList.contains('box')) return null;
    const w = el.offsetWidth, h = el.offsetHeight;
    return w > 0 && h > 0 ? { w, h } : null;
  }
  // The chart stands in the rose's unit (R1801), and the heat legend left
  // for the legend card (R1806).
  return null;
}

/**
 * The chart's slot (R1801): the square of the disc in the centre of the
 * rose's slot. On a desktop the round unit stands at the top left again,
 * where the square chart stood from R1433 and R1677 (R2879).
 */
export function discSlot(rose: Slot, sheet: boolean): Slot {
  const d = sheet ? DISC_PX_SHEET : DISC_PX;
  return { x: rose.x + (rose.w - d) / 2, y: rose.y + (rose.h - d) / 2, w: d, h: d };
}

/**
 * The clip of the rose press while the chart is open (R1801): the rose's
 * slot less the disc, as one even-odd path in the slot's own px. A press
 * on the disc then falls through to the canvas, which moves the camera as
 * a press on the minimap does; a press on the rim or a corner goes home.
 * `none` while the rose stands alone.
 */
export function roseClip(rose: Slot | undefined, disc: Slot | undefined): string {
  if (!rose || !disc) return 'none';
  const r = disc.w / 2;
  const cx = disc.x - rose.x + r;
  // The slot's y runs up from the floor and a path's y runs down.
  const cy = rose.y + rose.h - (disc.y + disc.h) + r;
  const n = (v: number) => String(Math.round(v * 100) / 100);
  return `path(evenodd, "M0 0H${n(rose.w)}V${n(rose.h)}H0Z`
    + `M${n(cx - r)} ${n(cy)}A${n(r)} ${n(r)} 0 1 0 ${n(cx + r)} ${n(cy)}`
    + `A${n(r)} ${n(r)} 0 1 0 ${n(cx - r)} ${n(cy)}Z")`;
}

// --- the context slot (ui-map-interface.md §12.0, round 083 cards 10 and 13) --

/** The inset of the telephone context sheet from each side and over the
 *  sheet's bar. */
export const CTX_INSET = LAYOUT_CTX_INSET;
/** The width of a card on a desktop (`--w-card`). */
export const W_CARD = WIDTH_CARD;

/** The place of the context slot: `l` from the left, `b` from the bottom,
 *  `w` wide, in chrome px. */
export interface CtxSlot { l: number; b: number; w: number }

/**
 * Where the card of a thing stands (round 083 cards 10 and 13).
 *
 * A desktop stands it at the bottom-left of the map, one gap over the
 * console (the chat box; the round unit stands at the top left, R2879,
 * and VIEW on the chart strip, R2904), `--w-card` wide. A telephone stands
 * it as the context sheet over the sheet's bar (the tab bar, the queue
 * line and the siege row), `CTX_INSET` in from each side and over the bar,
 * so the tab bar stays in view under it. A mirrored desktop (R2883) passes
 * `right`, the dock's left edge, and the card stands at the bottom right
 * of the map, one pad left of the dock. It is pure, like `packCluster`.
 */
export function ctxSlot(sheet: boolean, vw: number, clusterH: number, barH: number, right?: number): CtxSlot {
  if (sheet) return { l: CTX_INSET, b: barH + CTX_INSET, w: Math.max(0, vw - 2 * CTX_INSET) };
  if (right === undefined) return { l: PAD, b: clusterH + GAP, w: Math.max(0, Math.min(W_CARD, vw - 2 * PAD)) };
  const w = Math.max(0, Math.min(W_CARD, right - 2 * PAD));
  return { l: flipX(PAD, w, right), b: clusterH + GAP, w };
}

/**
 * The floor of the console on a telephone (R1747): one pad over the
 * sheet's bar, or one pad over the closed context sheet while one stands,
 * so VIEW and the round unit stay in reach over the peek (087 card 01).
 */
export function sheetConsoleFloor(barH: number, ctxClosedH: number | null): number {
  if (ctxClosedH === null || ctxClosedH <= 0) return barH + PAD;
  return barH + CTX_INSET + ctxClosedH + PAD;
}

/** The height of the closed context sheet, or null while none stands. */
let ctxClosedH: number | null = null;

/** Notes the closed height of the context sheet from its element, and
 *  answers whether it changed. A hidden sheet stands no more; a raised one
 *  keeps the closed height. */
function noteContextSheet(el: HTMLElement): boolean {
  const was = ctxClosedH;
  if (!SHEET_MQ.matches || el.offsetHeight <= 0 || el.classList.contains('ctx-wait')) ctxClosedH = null;
  else if (el.classList.contains('closed')) ctxClosedH = el.offsetHeight;
  return ctxClosedH !== was;
}

// --- the landscape rail (ui-map-interface.md §12.0, round 083 card 15) ----

/** The width of the rail, less the safe inset, and of the sheet beside it. */
export const RAIL_PX = LAYOUT_RAIL_PX;
export const RAIL_PANEL_PX = LAYOUT_RAIL_PANEL_PX;
/** The pad at the head and at the foot of the rail. */
export const RAIL_PAD_PX = LAYOUT_RAIL_PAD_PX;

/** The least chrome height of a rail cell whose caps show their words. */
const RAIL_WORD_PX = LAYOUT_RAIL_WORD_PX;
/** The scale bar's greatest width (`scale_bar_px`). */
const SCALE_BAR_PX = LAYOUT_SCALE_BAR_PX;

/**
 * Do the cells of the rail keep the finger target in this height (R1828)?
 * `cells` is the six tabs and the two caps, which stand with no gap. The
 * target is a length on the glass, so the rail counts it in screen px: a
 * cell holds the target divided by the UI scale `scale` in chrome px
 * (round 090 card 09 C), and the rail stands at 120 and 140 too. A
 * telephone that leaves too little height keeps the bottom sheet, because
 * a cell under the target breaks §12.0.
 */
export function railFits(chromeH: number, ins: Insets, cells: number, scale = 1): boolean {
  if (cells <= 0) return false;
  return chromeH - ins.t - ins.b >= cells * TARGET_FINGER / scale + 2 * RAIL_PAD_PX;
}

/** Does the shell stand the rail: a telephone on its side whose rail fits? */
export function hasRail(
  sheet: boolean, landscape: boolean, chromeH: number, ins: Insets, cells: number, scale = 1,
): boolean {
  return sheet && landscape && railFits(chromeH, ins, cells, scale);
}

/**
 * Do the caps of the rail show their words (round 090 card 09 C)? The
 * cells share the height of the rail, and a cell under `rail_word_px`
 * chrome px shows the icon of a cap alone, as the portrait tab bar does
 * on a narrow chrome (R1556).
 */
export function railWords(chromeH: number, ins: Insets, cells: number): boolean {
  if (cells <= 0) return true;
  return (chromeH - ins.t - ins.b - 2 * RAIL_PAD_PX) / cells >= RAIL_WORD_PX;
}

/** The right edge of the column beside the rail, in chrome px from the
 *  left: the rail with the left inset, and the column. */
export function railColumnRight(ins: Insets): number {
  return RAIL_PX + ins.l + RAIL_PANEL_PX;
}

/**
 * Does the context card stand at the right edge beside the column (round
 * 090 card 09 C)? The card and a pad at each side must stand free right of
 * the column; else the card stands over the column's bar.
 */
export function railCardBeside(vw: number, ins: Insets): boolean {
  return vw - ins.r - railColumnRight(ins) >= W_CARD + 2 * PAD;
}

/**
 * The context slot of the rail (round 083 card 15, round 090 card 09 C).
 * Where it has room, the card stands at the right edge inside the safe
 * area, a card wide, one pad over the home indicator, and the raised card
 * takes the height under the readout line. Where it has none, the card
 * stands over the column's bar as the context sheet of a portrait
 * telephone stands over its bar, `ctx_inset` in from each side of the
 * column. `barH` is the height of the column's bar over the home
 * indicator.
 */
export function railCtxSlot(vw: number, ins: Insets, barH = 0): CtxSlot {
  if (railCardBeside(vw, ins)) return { l: Math.max(0, vw - ins.r - PAD - W_CARD), b: ins.b + PAD, w: W_CARD };
  return { l: RAIL_PX + ins.l + CTX_INSET, b: ins.b + barH + CTX_INSET, w: RAIL_PANEL_PX - 2 * CTX_INSET };
}

/** The place of the round unit beside the rail, and whether the scale bar
 *  beside it has room. */
export interface RailUnit { slot: Slot; bar: boolean }

/**
 * The round unit beside the rail (round 090 cards 02 B and 09 C). It
 * stands over the column's bar where it fits under the readout strip
 * (`top` is the strip's height) with a pad. Where it does not fit (the
 * chat band takes the column, or a high UI scale leaves too little
 * height), it stands right of the column under the strip. The scale bar
 * stands beside it only where `scale_bar_px` and a pad still stand free
 * inside the safe right edge. It is pure, like `packCluster`.
 */
export function railUnit(s: Slot, o: { vw: number; vh: number; top: number; ins: Insets }): RailUnit {
  if (s.y + s.h + PAD <= o.vh - o.top) return { slot: s, bar: true };
  const x = railColumnRight(o.ins) + PAD;
  const slot = { x, y: o.vh - o.top - PAD - s.h, w: s.w, h: s.h };
  return { slot, bar: x + s.w + GAP + SCALE_BAR_PX + PAD <= o.vw - o.ins.r };
}

/**
 * The round unit of a desktop (R2879): at the top left, `PAD` in from the
 * left edge and one pad under `foot`, the foot of the chrome on the top
 * edge (`TOP_CHROME`), as the unit beside
 * the rail stands under the readout strip. The unit grows and shrinks
 * downward from that top, so the top edge holds when the chart opens or
 * shuts. A mirrored desktop (R2883) passes `right`, the dock's left edge,
 * and the unit stands at the top right, one pad left of the dock. It is
 * pure, like `packCluster`.
 */
export function deskUnit(size: { w: number; h: number }, vh: number, foot: number, right?: number): Slot {
  const x = right === undefined ? PAD : flipX(PAD, size.w, right);
  return { x, y: vh - foot - PAD - size.h, w: size.w, h: size.h };
}

/** The chrome on the top edge of a desktop that the round unit stands
 *  under (R2879): the readout line, the group bar under it, and the DEV
 *  STATS line while it shows (R1746). */
const TOP_CHROME = ['topbar', 'groupbar', 'stats'] as const;

/** The foot of the chrome on the top edge of a desktop, in chrome px from
 *  the top: the lowest foot of `TOP_CHROME`, 0 outside a browser. A layout
 *  offset, so the dock-in's transform does not move it. */
function topFoot(): number {
  if (typeof document === 'undefined') return 0;
  let foot = 0;
  for (const id of TOP_CHROME) {
    const el = document.getElementById(id);
    if (el && el.offsetHeight > 0) foot = Math.max(foot, el.offsetTop + el.offsetHeight);
  }
  return foot;
}

/**
 * The VIEW button of the rail: at the right edge inside the safe area, one
 * pad over the home indicator, and one pad over the closed context card
 * while one stands, so the thumb keeps it (R1747, 087 card 01).
 */
export function railButtons(vw: number, ins: Insets, ctxClosed: number | null, view: number = viewPx()): MapButtons {
  const b = ins.b + PAD + (ctxClosed && ctxClosed > 0 ? ctxClosed + PAD : 0);
  return { x: vw - ins.r - PAD - view, b, w: view, h: view };
}

/** The rail stands now. */
let railNow = false;
/** The context card stands at the right edge beside the column now. */
let ctxBeside = false;
/** The caps of the rail show their words now. */
let railWordsNow = true;
/** The scale bar has room beside the round unit now. */
let unitBar = true;

/** Does the rail stand now (the last recompute)? */
export function landscapeRail(): boolean { return railNow; }

/** Does the context card stand at the right edge beside the rail's column
 *  now (round 090 card 09 C)? */
export function railCtxBeside(): boolean { return railNow && ctxBeside; }

/** The cells of the rail: the buttons of the tab bar (the six tabs and the
 *  two caps of the sheet form factor), or 0 with no tab bar. */
function railCells(): number {
  if (typeof document === 'undefined') return 0;
  return document.getElementById('tabs')?.querySelectorAll('button').length ?? 0;
}

/** The rail's own width in chrome px, with the safe inset it pads. */
function railWidth(): number {
  const tabs = typeof document === 'undefined' ? null : document.getElementById('tabs');
  return tabs && tabs.offsetWidth > 0 ? tabs.offsetWidth : RAIL_PX + insets.l;
}

/**
 * Writes the classes of the rail on `#ui`: `rail` while it stands,
 * `ctx-beside` while the context card stands at the right edge beside the
 * column, `rail-short` while the caps show no words, and `scalebar-off`
 * while the round unit stands right of the column with no room for the
 * scale bar (round 090 card 09 C).
 */
function publishRail(ui: HTMLElement) {
  ui.classList.toggle('rail', railNow);
  ui.classList.toggle('ctx-beside', railNow && ctxBeside);
  ui.classList.toggle('rail-short', railNow && !railWordsNow);
  ui.classList.toggle('scalebar-off', railNow && !unitBar);
  // The mirrored desktop (R2883): the stylesheet flips the inner alignment
  // of the pieces that changed sides.
  ui.classList.toggle('mirror', mirrorNow);
}

/** Publishes the geometry to CSS. One `setProperty` per figure on `#ui`,
 *  which every panel inherits from. */
function publish() {
  const ui = document.getElementById('ui');
  if (!ui) return;
  const px = (n: number) => `${Math.round(n)}px`;
  const view = viewPx();
  ui.style.setProperty('--console-b', px(floorB));
  ui.style.setProperty('--cluster-h', px(clusterH));
  // The context slot (§12.0): every card that explains a thing stands
  // here, on both form factors.
  const sheet = SHEET_MQ.matches;
  publishRail(ui);
  const ctx = railNow ? railCtxSlot(vw(), insets, sheetBarH())
    : ctxSlot(sheet, vw(), clusterH, sheet ? sheetBarH() : 0, mirrorNow ? edgeR : undefined);
  ui.style.setProperty('--ctx-l', px(ctx.l));
  ui.style.setProperty('--ctx-b', px(ctx.b));
  ui.style.setProperty('--ctx-w', px(ctx.w));
  ui.style.setProperty('--unit-t', px(unitT));
  // The desktop context card may stand over the round unit, and it stops
  // one pad under the chrome on the top edge (R2904).
  ui.style.setProperty('--top-foot', px(topF));
  // The map buttons (R1747): the box of the VIEW button, its size and the
  // gutter between it and the VIEW panel. The compass rose's press stands
  // on the rose's own slot, `--rose-x/-b/-w/-h` below (R1758).
  ui.style.setProperty('--mapbtns-x', px(buttons.x));
  ui.style.setProperty('--mapbtns-r', px(vw() - buttons.x - buttons.w));
  ui.style.setProperty('--mapbtns-b', px(buttons.b));
  ui.style.setProperty('--mapbtns-t', px(buttons.b + buttons.h));
  ui.style.setProperty('--view-px', px(view));
  ui.style.setProperty('--mapbtns-gap', px(BUTTON_GAP));
  // The rose press leaves the disc of the round minimap to the canvas.
  ui.style.setProperty('--rose-clip', openNow.map ? roseClip(slots.rose, slots.map) : 'none');
  // Animation A collapses a panel onto the VIEW button, the one control
  // that opens it again.
  const cx = buttons.x + buttons.w / 2;
  const cy = buttons.b + view / 2;
  for (const u of CONSOLE_UNITS) {
    const s = slots[u];
    if (!s) continue;
    ui.style.setProperty(`--${u}-x`, px(s.x));
    // The gap from the right edge of the viewport, which a piece of the
    // mirrored desktop stands on (R2883).
    ui.style.setProperty(`--${u}-right`, px(vw() - s.x - s.w));
    ui.style.setProperty(`--${u}-b`, px(s.y));
    ui.style.setProperty(`--${u}-w`, px(s.w));
    ui.style.setProperty(`--${u}-h`, px(s.h));
    // Animation A's `transform-origin`, stated in the panel's own box. The
    // y axis flips, because a transform origin is measured from the top and
    // a slot from the bottom.
    ui.style.setProperty(`--${u}-ox`, px(cx - s.x));
    ui.style.setProperty(`--${u}-oy`, px(s.h + s.y - cy));
  }
}

/**
 * Recomputes the console and the map buttons, and tells everybody who
 * reads them.
 *
 * Event-driven, never on a timer (§10.3): a resize, a rotation, a change of
 * the dock's bar, a press in the VIEW panel, and a legend whose text changed
 * height. Nothing here runs per frame.
 */
export function relayoutCluster() {
  const sheet = SHEET_MQ.matches;
  // The rail (R1828) decides the floor, so it is decided first. Its class
  // goes on #ui at once, because the bar the floor measures is the bar of
  // the rail's form.
  const cells = railCells();
  mirrorNow = mirrorOn && !sheet;
  railNow = hasRail(sheet, RAIL_MQ.matches, vh(), insets, cells, uiZoom());
  ctxBeside = railNow && railCardBeside(vw(), insets);
  railWordsNow = railWords(vh(), insets, cells);
  const uiEl = typeof document === 'undefined' ? null : document.getElementById('ui');
  if (uiEl) publishRail(uiEl);
  // The console keeps one place for the whole of a session (R237): it
  // used to stand on `#dock`, so every change of the sheet's height moved
  // the chat box, the chart and the legend with it — a control that a
  // thumb had just found walked away from the thumb. It stands on
  // `consoleFloor()` instead, which moves with the form factor and the
  // bar's height and never with the sheet's stop. In the sheet form factor
  // that floor is one pad over the sheet's own bar (R318, R1747): the
  // console and the map buttons clear the collapsed sheet, and a sheet that
  // is raised is drawn over them, which the stylesheet states with a
  // z-index. No row of toggles stands under the units any more (R1747).
  floorB = consoleFloor();
  openNow = readConsole();
  const width = vw();
  const dockL = Math.max(0, width - dockW);
  edgeR = mirrorNow ? dockL : width;
  const view = viewPx();
  const open: UnitSize[] = [];
  const sizes: Partial<Record<ConsoleUnit, { w: number; h: number }>> = {};
  // The chart is the disc of the rose's unit (R1801): the unit takes the
  // square of the round minimap while the chart is open.
  for (const u of PACKED_UNITS) {
    const s = unitSize(u, sheet, openNow.map);
    if (!s) continue;
    sizes[u] = s;
    // A desktop stands the round unit at the top left (R2879), so the
    // chat box keeps the lower-left corner alone.
    if (openNow[u] && (sheet || u !== 'rose')) open.push({ unit: u, ...s });
  }
  topF = sheet ? 0 : topFoot();
  const deskSlot = !sheet && sizes.rose
    ? deskUnit(sizes.rose, vh(), topF, mirrorNow ? edgeR : undefined) : undefined;
  // A telephone keeps the right edge free for the VIEW button. A desktop
  // packs the chat box alone in the lower-left corner (R1838); VIEW stands
  // on the chart strip at the other corner (R2904), so the packer keeps
  // no box free for it.
  // The rail of a telephone on its side keeps the left edge (R1828), and
  // VIEW stands at the right edge inside the safe area.
  const room: PackRoom = railNow
    ? { vw: width, floor: floorB, left: railWidth(), trail: view + GAP + insets.r }
    : sheet
      ? { vw: width, floor: floorB, trail: view + GAP }
      : { vw: edgeR, floor: floorB, mirror: mirrorNow };
  const packed = packCluster(open, room);
  // Beside the rail the round unit stands right of the column where it
  // does not fit over the bar (round 090 cards 02 B and 09 C).
  unitBar = true;
  if (railNow && packed.slots.rose) {
    const top = typeof document === 'undefined' ? 0 : document.getElementById('topbar')?.offsetHeight ?? 0;
    const u = railUnit(packed.slots.rose, { vw: width, vh: vh(), top, ins: insets });
    packed.slots.rose = u.slot;
    unitBar = u.bar;
  }
  const head = railNow ? railWidth() + PAD : PAD;
  const next: Partial<Record<ConsoleUnit, Slot>> = {};
  for (const u of CONSOLE_UNITS) {
    // A closed unit keeps the slot it had, because that is the box its
    // collapse animation runs out of. A unit that never had one is parked
    // at the head of the first row.
    const s = (u === 'rose' ? deskSlot : undefined) ?? packed.slots[u] ?? (slots[u] && sizes[u] ? { ...slots[u]!, ...sizes[u]! } : slots[u])
      ?? (sizes[u] ? { x: mirrorNow ? flipX(head, sizes[u]!.w, edgeR) : head, y: floorB, ...sizes[u]! } : undefined);
    if (s) next[u] = s;
  }
  // VIEW rides on what is open, and a closed unit lifts nothing.
  buttons = railNow ? railButtons(width, insets, ctxBeside ? ctxClosedH : null, view)
    : sheet ? placeMapButtons(packed.slots, width, floorB, true, view)
      : stripButtons(packed.slots, dockL, floorB, stripTop, view, mirrorNow);
  if (next.rose) next.map = discSlot(next.rose, sheet);
  else delete next.map;
  slots = next;
  // The context slot clears the packed units alone: the desktop VIEW
  // stands on the chart strip (R2904) and the round unit of a desktop at
  // the top (R2879), so neither adds height here.
  clusterH = packed.height;
  unitT = deskSlot && openNow.rose ? vh() - deskSlot.y + GAP : 0;
  publish();
  for (const fn of clusterListeners) fn();
}

/**
 * Wires the console up. `read` is the overlay's answer to "which units are
 * open" — the console holds no state of its own, because the settings
 * store is where the answer of a VIEW tile lives.
 */
export function installCluster(read: () => ConsoleState) {
  readConsole = read;
  if (!clusterWired) {
    clusterWired = true;
    // A rotation can cross the form-factor line without resizing anything a
    // resize would report. Everything else the console follows arrives on
    // `installLayoutVars`'s observer, so this module puts no listener on the
    // window and an overlay has none to take off again.
    SHEET_MQ.addEventListener('change', relayoutCluster);
    // A rotation inside the sheet form factor (portrait to landscape)
    // leaves the sheet query as it was and turns the rail on (R1828).
    RAIL_MQ.addEventListener('change', relayoutCluster);
    // A hybrid machine can change pointer without changing size, and the
    // size of the VIEW button is a function of the pointer.
    COARSE_MQ.addEventListener('change', relayoutCluster);
    // A new UI scale changes the viewport in chrome px (R1556).
    onUiScale(relayoutCluster);
  }
  relayoutCluster();
}

/**
 * The MIRRORED LAYOUT setting (R2883, ui-map-interface.md §12.0): on, a
 * desktop flips the map edge left for right. The round unit stands at the
 * top right, the console (the chat box and the context card) at the bottom
 * right, both one pad left of the dock; the notices go top left, and the
 * chart stack with VIEW on it bottom left (R2904). A telephone and the rail keep their layout.
 * The overlay hands the setting in at boot and after each write; a change
 * lays the console out again at once.
 */
export function setMirrorDesk(on: boolean): void {
  if (on === mirrorOn) return;
  mirrorOn = on;
  if (clusterWired) relayoutCluster();
}

/**
 * The chart stack hands in the top of the chart strip (R2904): chrome px
 * from the bottom of the viewport, or null while no card of the strip
 * stands. The desktop VIEW stands on it. Only a real change lays the
 * console out again, and the stack's answer to that layout hands in the
 * same top, so the two never loop: the column's floor does not count VIEW.
 */
export function setStripTop(top: number | null): void {
  const next = top === null ? null : Math.round(top);
  if (next === stripTop) return;
  stripTop = next;
  if (clusterWired && !SHEET_MQ.matches) relayoutCluster();
}

/** Does the desktop stand mirrored now (the last recompute)? */
export function mirrorDesk(): boolean { return mirrorNow; }

/** Called after every recompute. The chart subscribes; CSS does not need
 *  to, because it reads the published properties by itself. */
export function onCluster(fn: () => void) { clusterListeners.push(fn); }

/**
 * One unit's box in screen coordinates — x and y from the **top left**,
 * which is what the canvas chart wants. The chart is the only reader: it
 * cannot see a CSS custom property. The box is in viewport px, the slot
 * times the UI scale (R1556), because the canvas stands outside `#ui` and
 * its slot must still match the map buttons and the panels around it.
 *
 * `visible` says whether the unit is open. A box is handed back either way,
 * because a closing unit still has to know where it is closing from.
 */
export function clusterBox(unit: ConsoleUnit):
{ x: number; y: number; w: number; h: number; visible: boolean } {
  const s = slots[unit];
  if (!s) return { x: PAD, y: 0, w: 0, h: 0, visible: false };
  const z = uiZoom();
  return { x: s.x * z, y: (vh() - s.y - s.h) * z, w: s.w * z, h: s.h * z, visible: unitOpen(unit) };
}

/** Is a unit open now? The chart is on the glass while it is open and
 *  its unit stands (R1801). */
function unitOpen(unit: ConsoleUnit): boolean {
  return unit === 'map' ? openNow.map && openNow.rose : !!openNow[unit];
}

/** The box of the map buttons at the last recompute, in chrome px
 *  (R1747). The stylesheet reads the same box as `--mapbtns-*`. */
export function mapButtonsBox(): MapButtons {
  return { ...buttons };
}

/**
 * The boxes the console holds on the floor at the last recompute, in
 * chrome px with `y` from the floor of the screen (R2057): every open unit,
 * and the VIEW button of a telephone. The chart stack stands its column
 * over the boxes it would cover, so the legend card never lies on the chat
 * box. The round unit of a desktop stands at the top left (R2879) and
 * lifts no column, and the desktop VIEW stands on the column itself
 * (R2904), so both are left out there.
 */
export function consoleBoxes(): Slot[] {
  const out: Slot[] = [];
  const sheet = SHEET_MQ.matches;
  for (const u of CONSOLE_UNITS) {
    const s = slots[u];
    if (!sheet && (u === 'rose' || u === 'map')) continue;
    if (s && unitOpen(u) && s.w > 0 && s.h > 0) out.push(s);
  }
  if (sheet && buttons.w > 0) out.push({ x: buttons.x, y: buttons.b, w: buttons.w, h: buttons.h });
  return out;
}

/**
 * The floor of a column of cards that spans `left` to `right` in chrome
 * px (R2057): `floor`, or one `gap` over the top of the highest box under
 * that span. A box beside the span lifts nothing. Pure.
 */
export function floorOver(
  left: number, right: number, boxes: readonly Slot[], floor: number, gap: number,
): number {
  let b = floor;
  for (const s of boxes) {
    if (s.x < right && s.x + s.w > left) b = Math.max(b, s.y + s.h + gap);
  }
  return b;
}
